import type React from 'react';
import { useEffect, useState } from 'react';
import {
  ACCOUNT_OPTION_FOUND_ATTRIBUTE,
  type AccountOption,
  findAccountOption,
  findAccountOptionTarget,
} from '../account-options';
import type { AccountTab } from './account-tabs';

const SELECTED_TAB = '[role="tab"][aria-selected="true"]';

/**
 * Takes the player to an option: focuses its control, scrolls it to the middle of the screen and highlights it until
 * focus leaves it, so it stands out however many options are around it.
 */
const reveal = (option: HTMLElement, control: HTMLElement, reducedMotion: boolean) => {
  control.focus({ preventScroll: true });
  option.scrollIntoView({ block: 'center', behavior: reducedMotion ? 'auto' : 'smooth' });
  option.setAttribute(ACCOUNT_OPTION_FOUND_ATTRIBUTE, '');

  const unmark = (event: FocusEvent) => {
    if (event.relatedTarget instanceof Node && option.contains(event.relatedTarget)) return;
    option.removeAttribute(ACCOUNT_OPTION_FOUND_ATTRIBUTE);
    option.removeEventListener('focusout', unmark);
  };
  option.addEventListener('focusout', unmark);
};

/**
 * Takes the player to a chosen option (see `reveal`) once its tab is on screen. The tab may arrive later (the app
 * moves to its route) and so may the option (the Profile tab loads first), so this waits for both, holding focus on
 * the selected tab meanwhile. It gives up as soon as the player moves focus elsewhere.
 *
 * Returns the function that chooses the option to focus.
 */
export const useOptionFocus = (
  root: React.RefObject<HTMLElement | null>,
  tab: AccountTab,
  shown: boolean,
  reducedMotion: boolean,
) => {
  const [pending, setPending] = useState<AccountOption>();

  // Runs after every render: any render may be the one that shows the option.
  useEffect(() => {
    const element = root.current;
    if (!pending || !shown || pending.tab !== tab || !element) return;

    const selectedTab = element.querySelector<HTMLElement>(SELECTED_TAB);
    const active = document.activeElement;
    if (active && active !== document.body && active !== selectedTab) {
      setPending(undefined);
      return;
    }

    const option = findAccountOption(element, pending.id);
    const control = findAccountOptionTarget(element, pending.id);
    if (option && control) {
      reveal(option, control, reducedMotion);
      setPending(undefined);
    } else {
      selectedTab?.focus();
    }
  });

  return setPending;
};
